import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, FormArray } from '@angular/forms';
import { SelectItem, Sidebar } from 'primeng/primeng';
import { TagService } from '../../../../services/tag.service';
import { StrtInitiative } from '../../../../../shared/models/plan/StrtInitiative';
import { StrtActivity } from '../../../../../shared/models/plan/StrtActivity';
import { StrtActivityTarget } from '../../../../../shared/models/plan/StrtActivityTarget';

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss']
})
export class ActivityComponent implements OnInit {
  readonly MAX_DESCRIPTION_LENGTH = 50;
  readonly MAX_PERSON_RESPONSIBLE_LENGTH = 50;
  readonly MAX_OTHER_RESOURCES_LENGTH = 100;
  readonly MAX_MEASURE_LENGTH = 300;
  readonly MAX_OUTCOME_LENGTH = 150;
  readonly MAX_TARGETS_PER_ACTIVITY = 10;
  readonly MIN_FINANCIAL_RESOURCES = 0;
  readonly MAX_FINANCIAL_RESOURCE_DOLLARS = 999999999;

  readonly adEdSelectItems: SelectItem[] = [{label: 'Select a Tag', value: undefined}];

  @Output() create = new EventEmitter<StrtActivity>();
  @Output() edit = new EventEmitter<StrtActivity>();
  @Output() cancel = new EventEmitter<void>();

  private _activityForm: FormGroup;
  private _isActive = false;
  private isEdit = false;

  private initiative: StrtInitiative;
  private pristineActivity: StrtActivity;

  selectedActivityTargets: StrtActivityTarget[] = [];

  targetDate = new FormControl('');
  outcome = new FormControl('');

  constructor(private formBuilder: FormBuilder, private tagService: TagService) { }

  ngOnInit() {
    this.createForm();
    this.tagService.getAdEDTags().subscribe(it => {
      this.adEdSelectItems.push(...this.tagService.getTagSelectItems(it));
    });
  }

  get headerContent() {
    return this.isEdit ? 'Edit Activity Information' : 'Add Activity Information';
  }

  get targetDates(): FormArray {
    return <FormArray>this.activityForm.controls.targetDates;
  }

  get measure(): FormArray {
    return <FormArray>this.activityForm.controls.measure;
  }

  get descriptionCharCount() {
    return this.activityForm.controls.description.value.length;
  }

  get responsibleCharCount() {
    return (this.activityForm.controls.personResponsible.value || '').length;
  }

  enforceFinResourceMax(event) {
    console.log(`Validate Fin Resource: `, event);
    const field = this.activityForm.get('requiredFinances');
    if (field.value > this.MAX_FINANCIAL_RESOURCE_DOLLARS) {
      field.setValue(this.MAX_FINANCIAL_RESOURCE_DOLLARS);
    }
  }

  get otherResourcesCharCount() {
    return this.activityForm.controls.otherResources.value.length;
  }

  get measureLength() {
    return (this.measure.value || '').length;
  }

  get outcomeLength() {
    return (this.outcome.value || '').length;
  }

  isAddTargetCycleDisabled() {
    return this.isTargetCycleCountAtMax || this.targetDate.invalid || this.outcome.invalid;
  }

  createActivity(initiative: StrtInitiative) {
    this._isActive = true;
    this.isEdit = false;
    this.initiativeToForm(initiative);
  }

  editActivity(activity: StrtActivity, initiative: StrtInitiative) {
    this.isActive = true;
    this.isEdit = true;
    this.activityToForm(activity, initiative);
  }

  get isTargetCycleCountAtMax() {
    return this.selectedActivityTargets.length === this.MAX_TARGETS_PER_ACTIVITY;
  }

  get activityForm() {
    return this._activityForm;
  }

  get isSubmitDisabled() {
    return !(this.activityForm.status === 'VALID' &&
      !this.activityForm.pristine &&
      this.activityForm.dirty);
  }

  get isActive() {
    return this._isActive;
  }

  set isActive(isActive) {
    this._isActive = isActive;
  }

  get tag() {
    return this.activityForm.get('tag');
  }

  onShowSidebar(sidebar: Sidebar) {
    sidebar.maskClickListener();
  }

  onHideSidebar() {
    this.resetForm();
    this.isEdit = false;
  }

  onSubmit() {
    const activity = this.formToActivity();
    if (this.isEdit) {
      this.edit.emit(activity);
    } else {
      this.create.emit(activity);
    }
  }

  onAddTarget(targetDate, expectedOutcome) {
    const activityId = this.pristineActivity ? this.pristineActivity.activityId : undefined;
    this.selectedActivityTargets = this.selectedActivityTargets.concat({activityId, targetDate, expectedOutcome});
    this.targetDates.markAsDirty();
    this.targetDate.reset({value: '', disabled: this.isTargetCycleCountAtMax});
    this.outcome.reset({value: '', disabled: this.isTargetCycleCountAtMax});
  }

  onDeleteTargetDate(rowIndex: number) {
    this.selectedActivityTargets = this.selectedActivityTargets.filter((it, index) => index !== rowIndex);
    this.targetDates.markAsDirty();
    if (this.isTargetCycleCountAtMax) {
      this.outcome.disable();
      this.targetDate.disable();
    } else {
      this.outcome.enable();
      this.targetDate.enable();
    }
  }

  private createForm() {
    this._activityForm = this.formBuilder.group({
      description: ['', [Validators.required]],
      tag: ['', [Validators.required]],
      personResponsible: [''],
      requiredFinances: ['', [Validators.min(this.MIN_FINANCIAL_RESOURCES), Validators.max(this.MAX_FINANCIAL_RESOURCE_DOLLARS)]],
      otherResources: [''],
      launchDate: ['', []],
      measure: [''],
      targetDates: this.formBuilder.array([]),
      sourceOfFunding: [''],
    });
    this.targetDate = new FormControl({value: '', disabled: this.isTargetCycleCountAtMax}, Validators.required);
    this.outcome = new FormControl({value: '', disabled: this.isTargetCycleCountAtMax}, Validators.required);
  }

  private resetForm() {
    this.pristineActivity = undefined;
    this.initiative = undefined;
    this.selectedActivityTargets = [];

    this.activityForm.reset({
      description: '',
      tag: '',
      personResponsible: '',
      requiredFinances: '',
      otherResources: '',
      measure: '',
      launchDate: '',
      sourceOfFunding: ''
    });
    this.targetDates.reset();
    this.targetDate.reset({value: '', disabled: this.isTargetCycleCountAtMax});
    this.outcome.reset({value: '', disabled: this.isTargetCycleCountAtMax});
  }

  private initiativeToForm(initiative: StrtInitiative) {
    this.resetForm();
    this.initiative = initiative;
  }

  private activityToForm(activity: StrtActivity, initiative: StrtInitiative) {
    this.pristineActivity = activity;
    this.initiative = initiative;
    this.selectedActivityTargets = [...activity.activityTargets];

    this.activityForm.patchValue({
      description: activity.description,
      tag: activity.tagId,
      personResponsible: activity.responsibleStakeHolder,
      requiredFinances: activity.financialResources,
      otherResources: activity.otherResources,
      measure: activity.measure || '',
      launchDate: activity.launchDate || '',
      sourceOfFunding: activity.sourceOfFunding
    });
    this.targetDates.reset();
    this.targetDate.reset({value: '', disabled: this.isTargetCycleCountAtMax});
    this.outcome.reset({value: '', disabled: this.isTargetCycleCountAtMax});
  }

  private formToActivity(): StrtActivity {
    const activityId = this.pristineActivity ? this.pristineActivity.activityId : undefined;
    return {
      activityId,
      initiativeId: this.pristineActivity ? this.pristineActivity.initiativeId : this.initiative.initiativeId,
      strategyId: this.pristineActivity ? this.pristineActivity.strategyId : this.initiative.strategyId,
      description: this.activityForm.controls.description.value,
      tagId: this.activityForm.controls.tag.value,
      activityTargets: this.selectedActivityTargets.map(it => this.createActivityTarget(activityId, it)),
      measure: this.measure.value,
      financialResources: this.activityForm.controls.requiredFinances.value,
      launchDate: this.activityForm.controls.launchDate.value,
      otherResources: this.activityForm.controls.otherResources.value,
      responsibleStakeHolder: this.activityForm.controls.personResponsible.value,
      sourceOfFunding: this.activityForm.controls.sourceOfFunding.value
    };
  }

  private createActivityTarget(activityId: number, activiyTarget: StrtActivityTarget) {
    return {activityId, expectedOutcome: activiyTarget.expectedOutcome, targetDate: activiyTarget.targetDate};
  }
}
